// Formularios de escritura del módulo POS.
//
// Viven aparte de las vistas para que `components.tsx` siga siendo la
// lectura y esto sea la escritura. Cada formulario habla directo con su
// endpoint y avisa al padre con `onDone` para que recargue.
//
// Dos decisiones que no son cosméticas:
//
// - Cambiar un precio NO edita el anterior: crea uno nuevo vigente desde
//   ahora. El texto del formulario lo dice, porque un administrador que
//   cree que está "corrigiendo" un precio y en realidad está creando otro
//   necesita saberlo.
// - Cancelar exige razón del catálogo. El botón está deshabilitado hasta
//   que se elige una, igual que el backend, que responde 404 si falta.

import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';

import { posApi, PosApiError } from './api';
import type { CancellationReason, LocationKind } from './types';

// ── Primitivas ──────────────────────────────────────────────────

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

export function PrimaryButton({
  children,
  busy,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }) {
  return (
    <button
      type="submit"
      {...rest}
      disabled={busy || rest.disabled}
      className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium
                 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5
                 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="text-xs text-red-500 mt-2 rounded-lg bg-red-500/10 border border-red-500/30 px-2.5 py-2">
      {error}
    </p>
  );
}

/** Panel plegable para no tener el formulario abierto siempre. */
export function Collapsible({
  label,
  children,
}: {
  label: string;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <GhostButton onClick={() => setOpen(true)}>
        <Plus className="w-3.5 h-3.5" />
        {label}
      </GhostButton>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <GhostButton onClick={() => setOpen(false)} aria-label="Cerrar">
          <X className="w-3.5 h-3.5" />
        </GhostButton>
      </div>
      {children(() => setOpen(false))}
    </div>
  );
}

const slugify = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// ── Alta de artículo ────────────────────────────────────────────

export function NewItemForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const item = await posApi.createItem({ name: name.trim(), slug: slugify(name) });
      // Un artículo sin precio no se puede cobrar, así que si el
      // administrador escribió uno, lo dejamos vigente de una vez.
      if (price.trim()) await posApi.setPrice({ item_id: item.id, amount: price.trim() });
      onDone();
    } catch (err) {
      setError(err instanceof PosApiError ? err.message : 'No se pudo crear el artículo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_140px_auto] sm:items-end">
      <Field label="Nombre">
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Taco"
          required
        />
      </Field>
      <Field label="Precio">
        <input
          className={inputClass}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputMode="decimal"
          placeholder="18.00"
        />
      </Field>
      <PrimaryButton busy={busy} disabled={!name.trim()}>
        Crear
      </PrimaryButton>
      <div className="sm:col-span-3">
        <FormError error={error} />
      </div>
    </form>
  );
}

// ── Cambio de precio ────────────────────────────────────────────

export function PriceEditor({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return <GhostButton onClick={() => setOpen(true)}>Cambiar precio</GhostButton>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await posApi.setPrice({ item_id: itemId, amount: amount.trim() });
      setOpen(false);
      setAmount('');
      onDone();
    } catch (err) {
      setError(err instanceof PosApiError ? err.message : 'No se pudo fijar el precio');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2 flex-wrap justify-end">
      <input
        className={`${inputClass} mt-0 w-28`}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputMode="decimal"
        placeholder="0.00"
        autoFocus
        required
      />
      <PrimaryButton busy={busy}>Guardar</PrimaryButton>
      <GhostButton onClick={() => setOpen(false)}>Cancelar</GhostButton>
      <p className="w-full text-right text-xs text-muted-foreground">
        Rige desde ahora. Lo ya vendido conserva el precio con el que se cobró.
      </p>
      <div className="w-full">
        <FormError error={error} />
      </div>
    </form>
  );
}

// ── Alta de punto ───────────────────────────────────────────────

export function NewLocationForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<LocationKind>('point_of_sale');
  const [color, setColor] = useState('#0FB913');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await posApi.createLocation({ name: name.trim(), slug: slugify(name), kind, color });
      onDone();
    } catch (err) {
      setError(err instanceof PosApiError ? err.message : 'No se pudo crear el punto');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_180px_100px_auto] sm:items-end">
      <Field label="Nombre">
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="LOMAS"
          required
        />
      </Field>
      <Field label="Tipo">
        <select
          className={inputClass}
          value={kind}
          onChange={(e) => setKind(e.target.value as LocationKind)}
        >
          <option value="point_of_sale">Punto de venta</option>
          <option value="warehouse">Almacén</option>
          <option value="office">Oficina</option>
        </select>
      </Field>
      <Field label="Color">
        <input
          type="color"
          className={`${inputClass} h-[38px] p-1`}
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />
      </Field>
      <PrimaryButton busy={busy} disabled={!name.trim()}>
        Crear
      </PrimaryButton>
      <div className="sm:col-span-4">
        <FormError error={error} />
      </div>
    </form>
  );
}

export function LocationActiveToggle({
  id,
  isActive,
  onDone,
}: {
  id: string;
  isActive: boolean;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await posApi.updateLocation(id, { is_active: !isActive });
      onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <GhostButton onClick={toggle} disabled={busy}>
      {busy && <Loader2 className="w-3 h-3 animate-spin" />}
      {isActive ? 'Desactivar' : 'Reactivar'}
    </GhostButton>
  );
}

// ── Cancelar una venta ──────────────────────────────────────────

export function CancelSaleControl({ saleId, onDone }: { saleId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [reasonId, setReasonId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || reasons.length) return;
    posApi
      .cancellationReasons()
      .then(setReasons)
      .catch((e: PosApiError) => setError(e.message));
  }, [open, reasons.length]);

  if (!open) return <GhostButton onClick={() => setOpen(true)}>Cancelar</GhostButton>;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await posApi.cancelSale(saleId, reasonId);
      setOpen(false);
      onDone();
    } catch (err) {
      setError(err instanceof PosApiError ? err.message : 'No se pudo cancelar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 w-full">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          className={`${inputClass} mt-0 max-w-xs`}
          value={reasonId}
          onChange={(e) => setReasonId(e.target.value)}
        >
          <option value="">Elige una razón…</option>
          {reasons.map((r) => (
            <option key={r.id} value={r.id}>
              {r.text}
            </option>
          ))}
        </select>
        {/* El backend responde 404 sin razón; no dejamos ni intentarlo. */}
        <PrimaryButton type="button" onClick={submit} busy={busy} disabled={!reasonId}>
          Confirmar cancelación
        </PrimaryButton>
        <GhostButton onClick={() => setOpen(false)}>Dejar así</GhostButton>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        La venta queda visible y tachada. No se borra.
      </p>
      <FormError error={error} />
    </div>
  );
}

// ── Cerrar el día ───────────────────────────────────────────────

export function CloseDayForm({
  locationId,
  businessDate,
  onDone,
}: {
  locationId: string;
  businessDate: string;
  onDone: () => void;
}) {
  const [cash, setCash] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await posApi.closeDay({
        location_id: locationId,
        business_date: businessDate,
        counted_cash: cash.trim(),
        received_by: receivedBy.trim(),
      });
      onDone();
    } catch (err) {
      setError(err instanceof PosApiError ? err.message : 'No se pudo cerrar el día');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
      <Field label="Efectivo contado">
        <input
          className={inputClass}
          value={cash}
          onChange={(e) => setCash(e.target.value)}
          inputMode="decimal"
          placeholder="0.00"
          required
        />
      </Field>
      <Field label="Quién recibe" hint="Opcional — queda en el registro del corte.">
        <input
          className={inputClass}
          value={receivedBy}
          onChange={(e) => setReceivedBy(e.target.value)}
          placeholder="Nombre de quien recibe el efectivo"
        />
      </Field>
      <PrimaryButton busy={busy} disabled={!cash.trim()}>
        Cerrar el día
      </PrimaryButton>
      <div className="sm:col-span-3">
        <p className="text-xs text-muted-foreground">
          El esperado lo calcula el servidor. Una vez cerrado, ese día deja de aceptar ventas y
          correcciones.
        </p>
        <FormError error={error} />
      </div>
    </form>
  );
}
